/**
 * Reviews Display JavaScript
 * Handles client-side sorting, pagination, and rendering of reviews
 */

class ReviewsDisplay {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.reviewsData = [];
    this.currentSort = 'newest';
    this.currentPage = 1;
    this.reviewsPerPage = 10;
    this.settings = {};
    
    this.init();
  }

  init() {
    this.loadData();
    this.bindEvents();
    this.renderReviews();
  }

  loadData() {
    const dataScript = document.getElementById(`reviews-data-${this.sectionId}`);
    if (!dataScript) return;

    try {
      const data = JSON.parse(dataScript.textContent);
      this.reviewsData = data.reviews || [];
      this.settings = data.settings || {};
      this.reviewsPerPage = this.settings.reviews_per_page || 10;
      
      // Update total count display
      const totalCountElement = document.querySelector(`#reviews-pagination-info-${this.sectionId} .total-count`);
      if (totalCountElement) {
        totalCountElement.textContent = this.reviewsData.length;
      }
    } catch (error) {
      console.error('Error parsing reviews data:', error);
    }
  }

  bindEvents() {
    const sortSelect = document.getElementById(`reviews-sort-${this.sectionId}`);
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        this.currentSort = e.target.value;
        this.currentPage = 1; // Reset to first page
        this.renderReviews();
      });
    }
  }

  sortReviews(reviews, sortType) {
    const sortedReviews = [...reviews];
    
    switch (sortType) {
      case 'newest':
        return sortedReviews.sort((a, b) => new Date(b.date_submitted) - new Date(a.date_submitted));
      
      case 'oldest':
        return sortedReviews.sort((a, b) => new Date(a.date_submitted) - new Date(b.date_submitted));
      
      case 'highest_rated':
        return sortedReviews.sort((a, b) => {
          const ratingDiff = (b.rating || 0) - (a.rating || 0);
          // If ratings are equal, sort by newest
          if (ratingDiff === 0) {
            return new Date(b.date_submitted) - new Date(a.date_submitted);
          }
          return ratingDiff;
        });
      
      default:
        return sortedReviews;
    }
  }

  paginateReviews(reviews) {
    const totalPages = Math.ceil(reviews.length / this.reviewsPerPage);
    const startIndex = (this.currentPage - 1) * this.reviewsPerPage;
    const endIndex = startIndex + this.reviewsPerPage;
    
    return {
      reviews: reviews.slice(startIndex, endIndex),
      totalPages,
      currentPage: this.currentPage,
      totalReviews: reviews.length,
      startIndex: startIndex + 1,
      endIndex: Math.min(endIndex, reviews.length)
    };
  }

  renderReviewCard(review) {
    const flagHtml = this.renderCountryFlag(review.country);
    const starsHtml = this.renderStars(review.rating || 0);
    const dateFormatted = this.formatDate(review.date_submitted);
    const productHtml = this.renderProductLinks(review.product);
    
    return `
      <div class="reviews-display__card">
        <div class="reviews-display__header">
          <div class="reviews-display__customer">
            ${flagHtml}
            <span class="reviews-display__customer-name">${this.escapeHtml(review.customer_name || 'Anonymous')}</span>
          </div>
          <div class="reviews-display__date">${dateFormatted}</div>
        </div>
        
        <div class="reviews-display__rating">
          ${starsHtml}
        </div>
        
        ${review.title ? `<h3 class="reviews-display__title">${this.escapeHtml(review.title)}</h3>` : ''}
        
        ${review.content ? `<div class="reviews-display__content rte">${this.renderRichText(review.content)}</div>` : ''}
        
        ${productHtml}
      </div>
    `;
  }

  renderProductLinks(products) {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return '';
    }

    const productLinks = products.map(product => {
      if (product && product.title && product.url) {
        const imageHtml = (product.featured_image && product.featured_image.url) ? 
          `<img src="${product.featured_image.url}" alt="${this.escapeHtml(product.featured_image.alt || product.title)}" class="reviews-display__product-image" loading="lazy">` : '';
        
        return `
          <a href="${product.url}" class="reviews-display__product-link">
            ${imageHtml}
            <span class="reviews-display__product-title">${this.escapeHtml(product.title)}</span>
          </a>
        `;
      } else if (typeof product === 'string') {
        // Handle legacy string format
        return `<span class="reviews-display__product-title">${this.escapeHtml(product)}</span>`;
      }
      return '';
    }).filter(link => link !== '');

    if (productLinks.length > 0) {
      return `<div class="reviews-display__product">${productLinks.join(', ')}</div>`;
    }

    return '';
  }

  renderCountryFlag(country) {
    // Handle null/undefined/empty country
    if (!country || typeof country !== 'string') {
      return '<span class="country-flag"><span class="country-flag__emoji">🌍</span></span>';
    }

    const countryMapping = {
      'germany': { code: 'de', emoji: '🇩🇪' },
      'deutschland': { code: 'de', emoji: '🇩🇪' },
      'united kingdom': { code: 'gb', emoji: '🇬🇧' },
      'uk': { code: 'gb', emoji: '🇬🇧' },
      'britain': { code: 'gb', emoji: '🇬🇧' },
      'england': { code: 'gb', emoji: '🇬🇧' },
      'united states': { code: 'us', emoji: '🇺🇸' },
      'usa': { code: 'us', emoji: '🇺🇸' },
      'america': { code: 'us', emoji: '🇺🇸' },
      'france': { code: 'fr', emoji: '🇫🇷' },
      'italy': { code: 'it', emoji: '🇮🇹' },
      'spain': { code: 'es', emoji: '🇪🇸' },
      'netherlands': { code: 'nl', emoji: '🇳🇱' },
      'holland': { code: 'nl', emoji: '🇳🇱' },
      'canada': { code: 'ca', emoji: '🇨🇦' },
      'australia': { code: 'au', emoji: '🇦🇺' },
      'south africa': { code: 'za', emoji: '🇿🇦' },
      'belgium': { code: 'be', emoji: '🇧🇪' },
      'austria': { code: 'at', emoji: '🇦🇹' },
      'switzerland': { code: 'ch', emoji: '🇨🇭' },
      'sweden': { code: 'se', emoji: '🇸🇪' },
      'norway': { code: 'no', emoji: '🇳🇴' },
      'denmark': { code: 'dk', emoji: '🇩🇰' },
      'finland': { code: 'fi', emoji: '🇫🇮' },
      'poland': { code: 'pl', emoji: '🇵🇱' },
      'czech republic': { code: 'cz', emoji: '🇨🇿' },
      'czechia': { code: 'cz', emoji: '🇨🇿' },
      'ireland': { code: 'ie', emoji: '🇮🇪' },
      'portugal': { code: 'pt', emoji: '🇵🇹' },
      'greece': { code: 'gr', emoji: '🇬🇷' },
      'japan': { code: 'jp', emoji: '🇯🇵' },
      'south korea': { code: 'kr', emoji: '🇰🇷' },
      'korea': { code: 'kr', emoji: '🇰🇷' },
      'brazil': { code: 'br', emoji: '🇧🇷' },
      'mexico': { code: 'mx', emoji: '🇲🇽' },
      'argentina': { code: 'ar', emoji: '🇦🇷' },
      'india': { code: 'in', emoji: '🇮🇳' },
      'china': { code: 'cn', emoji: '🇨🇳' },
      'russia': { code: 'ru', emoji: '🇷🇺' },
      'new zealand': { code: 'nz', emoji: '🇳🇿' }
    };

    const countryLower = country.trim().toLowerCase();
    const countryData = countryMapping[countryLower] || { code: '', emoji: '🌍' };
    
    if (countryData.code) {
      const flagUrl = `https://flagcdn.com/24x18/${countryData.code}.png`;
      return `
        <span class="country-flag" title="${this.escapeHtml(country)}">
          <img src="${flagUrl}" 
               alt="${this.escapeHtml(country)} flag" 
               width="24" 
               height="18" 
               loading="lazy"
               class="country-flag__image"
               onerror="this.style.display='none'; this.nextElementSibling.style.display='inline';">
          <span class="country-flag__emoji" style="display: none;">${countryData.emoji}</span>
        </span>
      `;
    } else {
      return `<span class="country-flag"><span class="country-flag__emoji">${countryData.emoji}</span></span>`;
    }
  }

  renderStars(rating) {
    const maxStars = 5;
    let starsHtml = '<div class="star-rating star-rating--medium"><div class="star-rating__stars">';
    
    for (let i = 1; i <= maxStars; i++) {
      const filled = i <= rating ? 'star-rating__star--filled' : 'star-rating__star--empty';
      const fillStyle = i <= rating ? 'fill: currentColor;' : 'fill: transparent;';
      
      starsHtml += `
        <span class="star-rating__star ${filled}">
          <svg width="16" height="16" viewBox="0 0 16 16" style="${fillStyle}" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1L10.163 5.38L15 6.12L11.5 9.5L12.326 14.32L8 12.08L3.674 14.32L4.5 9.5L1 6.12L5.837 5.38L8 1Z" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      `;
    }
    
    starsHtml += '</div></div>';
    return starsHtml;
  }

  renderRichText(content) {
    if (typeof content === 'string') {
      return content;
    }
    
    // Handle rich text JSON structure
    if (content && typeof content === 'object') {
      try {
        return this.parseRichTextJSON(content);
      } catch (error) {
        return content.toString();
      }
    }
    
    return '';
  }

  parseRichTextJSON(richText) {
    if (!richText || !richText.children) return '';
    
    return richText.children.map(child => {
      if (child.type === 'paragraph' && child.children) {
        const paragraphContent = child.children.map(textNode => textNode.value || '').join('');
        return `<p>${this.escapeHtml(paragraphContent)}</p>`;
      }
      return '';
    }).join('');
  }

  formatDate(dateString) {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  renderPagination(paginationData) {
    if (paginationData.totalPages <= 1) return '';
    
    let paginationHtml = '<div class="pagination">';
    
    // Previous button
    if (paginationData.currentPage > 1) {
      paginationHtml += `<button class="pagination__btn pagination__btn--prev" data-page="${paginationData.currentPage - 1}">Previous</button>`;
    }
    
    // Page numbers
    for (let i = 1; i <= paginationData.totalPages; i++) {
      const activeClass = i === paginationData.currentPage ? 'pagination__btn--active' : '';
      paginationHtml += `<button class="pagination__btn ${activeClass}" data-page="${i}">${i}</button>`;
    }
    
    // Next button
    if (paginationData.currentPage < paginationData.totalPages) {
      paginationHtml += `<button class="pagination__btn pagination__btn--next" data-page="${paginationData.currentPage + 1}">Next</button>`;
    }
    
    paginationHtml += '</div>';
    return paginationHtml;
  }

  bindPaginationEvents() {
    const paginationContainer = document.getElementById(`reviews-pagination-${this.sectionId}`);
    if (!paginationContainer) return;

    paginationContainer.addEventListener('click', (e) => {
      if (e.target.classList.contains('pagination__btn') && e.target.dataset.page) {
        this.currentPage = parseInt(e.target.dataset.page);
        this.renderReviews();
      }
    });
  }

  updatePaginationInfo(paginationData) {
    const infoElement = document.getElementById(`reviews-pagination-info-${this.sectionId}`);
    if (infoElement && paginationData.totalReviews > 0) {
      infoElement.innerHTML = `Showing ${paginationData.startIndex}-${paginationData.endIndex} of <span class="total-count">${paginationData.totalReviews}</span>`;
    }
  }

  renderReviews() {
    const gridElement = document.getElementById(`reviews-grid-${this.sectionId}`);
    const paginationElement = document.getElementById(`reviews-pagination-${this.sectionId}`);
    const emptyElement = document.getElementById(`reviews-empty-${this.sectionId}`);
    
    if (!gridElement) return;

    // Sort reviews
    const sortedReviews = this.sortReviews(this.reviewsData, this.currentSort);
    
    // Paginate reviews
    const paginationData = this.paginateReviews(sortedReviews);
    
    if (paginationData.totalReviews === 0) {
      // Show empty state
      gridElement.style.display = 'none';
      if (paginationElement) paginationElement.style.display = 'none';
      if (emptyElement) emptyElement.style.display = 'block';
      return;
    }

    // Hide empty state
    if (emptyElement) emptyElement.style.display = 'none';
    
    // Render reviews
    gridElement.innerHTML = paginationData.reviews.map(review => this.renderReviewCard(review)).join('');
    gridElement.style.display = 'grid';
    
    // Render pagination
    if (paginationElement) {
      const paginationHtml = this.renderPagination(paginationData);
      paginationElement.querySelector('.pagination-wrapper').innerHTML = paginationHtml;
      paginationElement.style.display = paginationData.totalPages > 1 ? 'flex' : 'none';
      
      // Bind pagination events
      this.bindPaginationEvents();
    }
    
    // Update pagination info
    this.updatePaginationInfo(paginationData);
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Find all review display sections
  const reviewsSections = document.querySelectorAll('[id^="reviews-data-"]');
  reviewsSections.forEach(section => {
    const sectionId = section.id.replace('reviews-data-', '');
    new ReviewsDisplay(sectionId);
  });
});

// Handle theme editor
document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event.detail.sectionId;
  if (document.getElementById(`reviews-data-${sectionId}`)) {
    new ReviewsDisplay(sectionId);
  }
});